/**
 * Stripe Checkout API
 * Handles domain purchases via Stripe Checkout
 * 
 * AD-20251115-CHECKOUT-FIX: Improved error message extraction to surface backend error messages
 */

export interface CheckoutItem {
  domain: string;
}

export interface CheckoutPayload {
  items: CheckoutItem[];
  email?: string;
  attribution?: Record<string, string>;
  quotedTotalCents?: number;
  origin?: string;
}

export interface CheckoutResponse {
  ok: boolean;
  url?: string;
  data?: { url?: string };
  error?: string;
  message?: string;
  code?: string;
  redirectUrl?: string; // Legacy compatibility
}

/**
 * Create Stripe checkout session for domain purchases
 * Handles single or multiple domain purchases
 */
export async function createStripeCheckout(payload: CheckoutPayload): Promise<string> {
  try {
    // Always include current origin to ensure correct redirect
    const payloadWithOrigin = {
      ...payload,
      origin: window.location.origin,
    };
    
    const response = await fetch('/api/checkout', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payloadWithOrigin),
    });

    if (!response.ok) {
      // AD-20251115-CHECKOUT-FIX: Improved error message extraction - prioritize message field
      let errorMessage = `HTTP ${response.status}`;
      let errorCode: string | undefined;
      try {
        const errorData = await response.json();
        // Prioritize message field, then error, then code
        errorMessage = errorData.message || errorData.error || errorData.code || errorMessage;
        errorCode = errorData.code;
      } catch (e) {
        // If response isn't JSON, try to get text
        try {
          const text = await response.text();
          if (text) {
            // Try to parse as JSON even if Content-Type wasn't set
            try {
              const parsed = JSON.parse(text);
              errorMessage = parsed.message || parsed.error || parsed.code || errorMessage;
              errorCode = parsed.code;
            } catch {
              // Not JSON, use text as-is
              errorMessage = text.substring(0, 200);
            }
          }
        } catch (textError) {
          // Fall back to status code
        }
      }
      // Include error code in message for dev debugging
      const finalMessage = errorCode && process.env.NODE_ENV === 'development' 
        ? `${errorMessage} (code: ${errorCode})`
        : errorMessage;
      throw new Error(finalMessage);
    }

    const data: CheckoutResponse = await response.json();
    
    if (!data.ok) {
      // AD-20251115-CHECKOUT-FIX: Prioritize message field for better error display
      const errorMessage = data.message || data.error || data.code || 'Checkout failed';
      throw new Error(errorMessage);
    }

    // Handle both response formats: { url } and { data: { url } }
    const checkoutUrl = data.url || data.data?.url;
    if (!checkoutUrl) {
      throw new Error('No checkout URL received from server');
    }

    return checkoutUrl;
  } catch (error) {
    console.error('Stripe checkout error:', error);
    throw new Error(error instanceof Error ? error.message : 'Checkout failed');
  }
}

/**
 * Create Stripe checkout session for LTO (Lease-to-Own) subscription purchases
 */
export async function createStripeLtoCheckout(
  domain: string,
  term: '12m' | '24m' | '36m',
  payload?: { email?: string; attribution?: Record<string, string> }
): Promise<string> {
  try {
    // Always include current origin to ensure correct redirect
    const response = await fetch('/api/checkout-lto', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        domain,
        term,
        ...payload,
        origin: window.location.origin,
      }),
    });

    if (!response.ok) {
      // AD-20251115-CHECKOUT-FIX: Improved error message extraction - prioritize message field
      let errorMessage = `HTTP ${response.status}`;
      let errorCode: string | undefined;
      try {
        const errorData = await response.json();
        // Prioritize message field, then error, then code
        errorMessage = errorData.message || errorData.error || errorData.code || errorMessage;
        errorCode = errorData.code;
      } catch (e) {
        // If response isn't JSON, try to get text
        try {
          const text = await response.text();
          if (text) {
            // Try to parse as JSON even if Content-Type wasn't set
            try {
              const parsed = JSON.parse(text);
              errorMessage = parsed.message || parsed.error || parsed.code || errorMessage;
              errorCode = parsed.code;
            } catch {
              // Not JSON, use text as-is
              errorMessage = text.substring(0, 200);
            }
          }
        } catch (textError) {
          // Fall back to status code
        }
      }
      // Include error code in message for dev debugging
      const finalMessage = errorCode && process.env.NODE_ENV === 'development' 
        ? `${errorMessage} (code: ${errorCode})`
        : errorMessage;
      throw new Error(finalMessage);
    }

    const data: CheckoutResponse = await response.json();
    
    if (!data.ok) {
      // AD-20251115-CHECKOUT-FIX: Prioritize message field for better error display
      const errorMessage = data.message || data.error || data.code || 'LTO checkout failed';
      throw new Error(errorMessage);
    }

    // Handle both response formats: { url } and { data: { url } }
    const checkoutUrl = data.url || data.data?.url;
    if (!checkoutUrl) {
      throw new Error('No checkout URL received from server');
    }

    return checkoutUrl;
  } catch (error) {
    console.error('Stripe LTO checkout error:', error);
    throw new Error(error instanceof Error ? error.message : 'LTO checkout failed');
  }
}

// Legacy compatibility - redirect to new function
export async function submitCartCheckout(payload: { items: Array<{ domain: string }>, email?: string, attribution?: Record<string, string> }): Promise<CheckoutResponse> {
  try {
    const url = await createStripeCheckout(payload);
    return { ok: true, redirectUrl: url };
  } catch (error) {
    return {
      ok: false,
      error: error instanceof Error ? error.message : 'Checkout failed',
    };
  }
}
